//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React, {useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import {useDropzone} from "react-dropzone";
import Button from "@material-ui/core/Button";
import FilesIcon from "@material-ui/icons/FileCopy";
import UploadMultipleFileSingleSubmitWithConfigurationButton from "src/components/loader/buttons/UploadMultipleFileSingleSubmitWithConfigurationButton";
import ConfigurationCheckBox from "eai-configurator-ui/components/configuration/base/ConfigurationCheckBox";
import ConfigurationComboBox from "eai-configurator-ui/components/configuration/base/ConfigurationComboBox";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Item from "@material-ui/core/Grid";
import ConfigurationTextField from "eai-configurator-ui/components/configuration/base/ConfigurationTextField";
import UploadIcon from "@material-ui/icons/Publish";
import * as Config from "eai-configurator-ui/components/configuration/utils/ConfigurationRegistry";

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
    width: "100%",
  },
  textField: {
    width: "100%",
  },
  error: {
    color: "red",
  },
  checkbox: {
    paddingLeft: 0,
  },
  paper: {
    padding: 10,
  },
  button: {
    margin: 4,
  },
  expandablePanelIcon: {
    margin: 4,
  },
  expandablePanel: {
    paddingLeft: 10,
  },
  input: {
    display: "none",
  },
  dropzone: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
  uploadContainer: {
    alignItems: 'stretch'
  },
  uploadItem: {
    display: 'flex'
  },
  uploadItemButton: {
    display: 'flex'
  },
  uploadPaper: {
    width: '100%',
    padding: 10,
    display: 'flex',
    flexDirection: 'column'
  },
  uploadContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  uploadBottom: {
    marginTop: 'auto'
  },
  uploadButtonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  buildTargetOption: {
    "& .MuiFormGroup-root": {
      marginLeft: '-11px'
    },
  }
}));
const BuildP6FilePage = ({disabled, navigateToResults, dropzoneOptions}) => {
  //region Input file
  const [selectedInputFiles, setSelectedInputFiles] = useState([]);

  const {
    getRootProps: getRootPropsInputFile,
    getInputProps: getInputPropsInputFile,
    open: handleOpenInputFileSelection,
  } = useDropzone({
    // accept: 'image/jpeg, image/png'
    onDrop: (files) => setSelectedInputFiles(files),
    ...dropzoneOptions,
  });

  const clearSelectedInputFiles = () => {
    setSelectedInputFiles([]);
  };

  const selectedInputFilesItems = selectedInputFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));
  //endregion

  //region Target file
  const [selectedTargetFiles, setSelectedTargetFiles] = useState([]);

  const [buildIntoExistingFile, setBuildIntoExistingFile] = React.useState(
    Config.fromPath("loader:buildP6:buildIntoExistingFile", true).data.value
  );
  const [loadIntoNewFile, setLoadIntoNewFile] = React.useState(
    Config.fromPath("loader:buildP6:loadIntoNewFile", true).data.value
  );

  const {
    getRootProps: getRootPropsTargetFile,
    getInputProps: getInputPropsTargetFile,
    open: handleOpenTargetFileSelection,
  } = useDropzone({
    // accept: 'image/jpeg, image/png'
    disabled: loadIntoNewFile,
    onDrop: (files) => setSelectedTargetFiles(files),
    ...dropzoneOptions,
  });

  const clearSelectedTargetFiles = () => {
    setSelectedTargetFiles([]);
  };

  const selectedTargetFilesItems = selectedTargetFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));
  //endregion

  //region All files handling
  const clearSelectedFiles = () => {
    clearSelectedInputFiles();
    clearSelectedTargetFiles();
  }
  const [selectImportFileUploadList, setSelectImportFileUploadList] = useState([]);

  useEffect(() => {
    setSelectImportFileUploadList([...selectedInputFiles, ...selectedTargetFiles]);
  }, [selectedInputFiles, selectedTargetFiles]);
  //endregion

  const handleBuildIntoExistingFileChange = (value) => {
    console.log("handleBuildIntoExistingFileChange: " + value);
    setLoadIntoNewFile(String(value) !== "true");
    setBuildIntoExistingFile(String(value) === "true");
  };
  const handleLoadIntoNewFileChanged = (value) => {
    console.log("handleLoadIntoNewFileChanged: " + value);
    setLoadIntoNewFile(String(value) === "true");
    setBuildIntoExistingFile(String(value) !== "true");
  };

  const classes = useStyles();

  return (
    <React.Fragment>
      <div>
        <Typography variant="body2" color="textSecondary">
          <h5>Build P6 Files</h5>
          <hr/>
        </Typography>

        <Grid container className={classes.uploadContainer} spacing={1}>
          <Grid item xs={12} className={classes.uploadItem}>
            <Paper className={classes.uploadPaper}>
              <div className={classes.uploadContent}>
                <Typography variant="body2" color="textSecondary">
                  <h6>1 - Excel source file</h6>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  This is the Excel source file that will be loaded into the destination of your preference. This has to be a P6-Loader compatible Excel file.
                </Typography>
              </div>
              <div className={classes.uploadBottom}>
                <React.Fragment>
                  <div {...getRootPropsInputFile({className: classes.dropzone})}>
                    <input {...getInputPropsInputFile()} />
                    <p>Drag 'n' drop some files here, or click to select files</p>
                  </div>
                  <aside>
                    <ul>{selectedInputFilesItems}</ul>
                  </aside>
                </React.Fragment>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  startIcon={<FilesIcon/>}
                  onClick={handleOpenInputFileSelection}
                >
                  Choose File(s)
                </Button>
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} className={classes.uploadItem}>
            <Paper className={classes.uploadPaper}>
              <Grid container className={classes.uploadContainer} spacing={1}>
                <Grid item xs={12} className={classes.uploadItem}>
                  <div className={classes.uploadContent}>
                    <Typography variant="body2" color="textSecondary">
                      <h6>2 - Target file</h6>
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6} className={classes.uploadItem}>
                  <Paper className={classes.uploadPaper}>
                    <Typography variant="body2" color="textSecondary">
                      <h6>Load into existing file</h6>
                    </Typography>
                    <div className={classes.uploadContent}>
                      <Typography variant="caption" color="textSecondary">
                        Use this option to load the Excel file into an existing file.
                        <br/>
                        You can use a XER, XML or SQLite database file (.db). The updated file will be available for download when the task is completed.
                      </Typography>
                    </div>
                    <div className={classes.uploadBottom}>
                      <React.Fragment>
                        <div {...getRootPropsTargetFile({className: classes.dropzone})}>
                          <input {...getInputPropsTargetFile()} />
                          <p>Drag 'n' drop some files here, or click to select files</p>
                        </div>
                        <aside>
                          <ul>{selectedTargetFilesItems}</ul>
                        </aside>
                      </React.Fragment>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        startIcon={<FilesIcon/>}
                        onClick={handleOpenTargetFileSelection}
                        disabled={loadIntoNewFile}
                      >
                        Choose File(s)
                      </Button>
                    </div>
                  </Paper>
                </Grid>
                {/*<Grid item xs={6} className={classes.uploadItem}>*/}
                {/*  <Paper className={classes.uploadPaper}>*/}
                {/*    <div className={classes.buildTargetOption}>*/}
                {/*      <UnboundCheckBox*/}
                {/*        path={`loader:buildP6:loadIntoNewFile`}*/}
                {/*        handleChanged={handleLoadIntoNewFileChanged}*/}
                {/*        checked={loadIntoNewFile}/>*/}
                {/*    </div>*/}
                {/*    <div className={classes.uploadContent}>*/}
                {/*      <Typography variant="caption" color="textSecondary">*/}
                {/*        Use this option to load the Excel file into a new file. Choose the proper template from the list below.*/}
                {/*        <br/>*/}
                {/*        You can customize the list of templates by clicking the [Manage Templates] button.*/}
                {/*      </Typography>*/}
                {/*      <ConfigurationAsyncComboBox*/}
                {/*        fullWidth*/}
                {/*        path={"loader:buildP6:loadIntoNewFileTemplate"}*/}
                {/*        url={"loader/loadIntoNewFileTemplates"}*/}
                {/*        autoSelect={false}*/}
                {/*        disabled={buildIntoExistingFile}*/}
                {/*        transformHttpResponse={(data) => {*/}
                {/*          return {*/}
                {/*            options: data.templates.map((template) => template.id),*/}
                {/*          };*/}
                {/*        }}*/}
                {/*        // handleChanged={handleSelectUsageDataSource}*/}
                {/*      />*/}
                {/*    </div>*/}
                {/*    <div className={classes.uploadBottom}>*/}
                {/*    </div>*/}
                {/*  </Paper>*/}
                {/*</Grid>*/}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} className={classes.uploadItemButton}>
            <Paper className={classes.uploadPaper}>
              <Typography variant="body2" color="textSecondary">
                <h6>3 - Upload</h6>
              </Typography>
              <UploadMultipleFileSingleSubmitWithConfigurationButton
                url="loader/p6BuildUploadExcel"
                className={classes.button}
                selectedFiles={selectImportFileUploadList}
                clearSelectedInputFiles={clearSelectedFiles}
                disabled={selectedInputFilesItems.length === 0 || disabled}
                handleSuccess={navigateToResults}
                icon={<UploadIcon/>}
                label={"Upload"}/>
              <Typography variant="caption" color="textSecondary">
                After making your selection, click the [Upload] button to submit the load operation
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <hr/>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Options</h6>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Use this area to customize the import options.
            <br/>
            To <b>delete</b> data in P6, there should be a column in the Excel file named "Delete Record" with the value "DELETE" in the records you wish to
            delete, and the [Enable Deletes] checkbox should be checked.
            <br/>
            If the [Enable Inserts] checkbox is unchecked, no new records are added to the database. If the [Enable Updates] checkbox, no updates are made to
            the database.
            <br/>
            If the [Load Codes] checkbox is unchecked, code columns, such as "Code():XXX" will be ignored. If the [Load UDFs] checkbox is unchecked, UDF
            columns, such as "UDF():XXX" are ignored.
            <br/>
            If the [Load Process One Row At a Time] checkbox is checked, then the P6-Loader will process one row of the Excel file at a time. This will
            severely
            impact the performance, but it can be helpful in case you are having errors loading the Excel file.
          </Typography>
          <Grid container style={{marginTop: 10}}>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:buildP6:enableDeletes"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:buildP6:enableInserts"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:buildP6:enableUpdates"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:buildP6:loadCodes"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:buildP6:loadUdfs"}/>
            </Item>
            <Item xs={4}>
              <ConfigurationCheckBox path={"loader:buildP6:processOneRowAtATime"}/>
            </Item>
          </Grid>
        </Paper>

        <hr/>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Unique lookup overwrite</h6>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Use this area to disable the unique lookup for hierarchical objects, if necessary.
            <br/>
            If a hierarchical object is being loaded, but a path is not being provided
            when the value is being loaded (for example, just a WBS code but not the whole path), the
            P6-Loader can find the object with just that code. In some cases, for example when creating hierarchies where there might be repetitions,
            this may not be wanted, and if the [Prevent Unique Lookups] option is checked, then no lookups using unique values will be performed.
          </Typography>
          <Grid container style={{marginTop: 10}}>
            <Item xs={6}>
              <ConfigurationCheckBox path={"loader:buildP6:preventUniqueLookups"}/>
            </Item>
          </Grid>
        </Paper>

        <hr/>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Hierarchy options</h6>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Use this area to customize the import options related to hierarchical objects.
            <br/>
            The option [Full or Classic] lets you choose the preferred column combination,
            if the Excel file has both the "FullPath" column, as well as the "Parent" and "Code" columns. The "FullPath"
            column is a combination of the parent and code columns, and if you are using it, then the full path can be specified in just one place.
            <br/>
            The option [Primary Separator] lets you specify the primary separator used in the spreadsheet being loaded.
            This must match the separator used in the "FullPath" column. The option [Additional Separators] lets you configure possible
            additional separators used in the file being loaded - for example, if a different separator was used to generate the file than the one this
            instance
            is configured to use.
          </Typography>
          <Grid container style={{marginTop: 10}}>
            <Item xs={3}>
              <ConfigurationComboBox path={"loader:buildP6:fullOrClassicLoadDynamicOptionPreferred"}/>
            </Item>
            <Item xs={3}>
              <ConfigurationTextField path={"loader:buildP6:importSeparator"}/>
            </Item>
            <Item xs={3}>
              <ConfigurationTextField path={"loader:buildP6:importSeparators"}/>
            </Item>
          </Grid>
        </Paper>
      </div>
    </React.Fragment>
  )
    ;
};
export default BuildP6FilePage;
